import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import { formatDynamicRoute } from 'utils/common/formatDynamicRoute';
import type { DefaultPopupProps } from 'utils/props';

import Button from 'components/Button';
import DeviceList from 'components/DeviceComponents/DeviceList';
import GroupNames from 'components/DeviceComponents/GroupNames';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import ErrorText from 'components/ErrorText';
import CustomTrashIcon from 'components/Icons/CustomTrashIcon';
import OrganizationsIcon from 'components/Icons/OrganizationsIcon/intex';
import Modal from 'components/Modal';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch, useAppSelector } from 'redux-store';
import { removeDeviceOverviewSelectedDevice } from 'redux-store/slices/ui';
import type { DeviceSerial } from 'redux-store/slices/ui/types';
import deleteDeviceThunk from 'redux-store/thunks/devices/deleteDeviceThunk';

interface Props extends DefaultPopupProps {
  serials: DeviceSerial[] | null;
}

const RemoveMultipleDevicesModal: FC<Props> = ({ onClose, serials, open }) => {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const deviceOrganizations = useAppSelector(s => s.deviceOrganizations.items);

  const recreatedPath = formatDynamicRoute(router.asPath, router.query);

  const removeDevices = useCallback(async () => {
    if (!serials?.length) return;

    try {
      setLoading(true);
      const result = await dispatch(
        deleteDeviceThunk(serials.join(',')),
      ).unwrap();

      if (!result.success) {
        setError(t('common:pages.settings.remove_device_error'));

        return;
      } else {
        setSuccess(true);
        setError(null);
        serials.forEach(serial => {
          dispatch(removeDeviceOverviewSelectedDevice(serial));
        });
        setTimeout(async () => {
          await router.replace(recreatedPath);
          onClose();
          setSuccess(false);
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  }, [serials, dispatch, t, onClose, router, recreatedPath]);

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ overflowY: 'auto' }}
        >
          <Box mb={2}>
            <Typography variant="h2" mb={2} textAlign="center">
              {t('common:pages.settings.remove_multiple_devices')}
            </Typography>
            <Typography variant="body1" textAlign="center">
              {t(
                deviceOrganizations.length > 0
                  ? 'common:pages.settings.remove_multiple_devices_with_org_description'
                  : 'common:pages.settings.remove_multiple_devices_description',
              )}
            </Typography>
          </Box>
          {deviceOrganizations.length > 0 ? (
            <Box mt={2} mb={2} width="100%">
              <Typography variant="body1" mb={1} fontWeight={600} pl={2}>
                {t('common:pages.settings.devices_in_org')}
              </Typography>
              <Box width="100%">
                {deviceOrganizations.map(item => (
                  <DeviceList
                    key={item.serial}
                    serial={item.serial}
                    rightComponent={
                      item.organizations.length > 0 ? (
                        <RightComponentWrapper>
                          <OrganizationsIcon
                            fontSize="small"
                            sx={{ color: 'grey.500' }}
                          />
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body1" noWrap color="grey.500">
                              <GroupNames items={item.organizations} />
                            </Typography>
                          </Box>
                        </RightComponentWrapper>
                      ) : null
                    }
                  />
                ))}
              </Box>
            </Box>
          ) : null}
          <Box display="flex" flexDirection="row" mt={3} gap={1} width="100%">
            <Button
              onClick={onClose}
              color="neutral"
              fullWidth
              variant="flat"
              size="large"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={removeDevices}
              fullWidth
              variant="flat"
              size="large"
              color="error"
              data-testid="submit"
            >
              <CustomTrashIcon sx={{ mr: 1 }} />
              {t('common:pages.settings.deleteMultipleDevicesButton')}
            </Button>
          </Box>
          <ErrorText error={error} mt={2} />
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t('common:pages.settings.multiple_devices_removed')}
        />
      )}
    </Modal>
  );
};

export default RemoveMultipleDevicesModal;
